import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Music } from "lucide-react";
import API from "../apiBase";

/* MUSICA DI SOTTOFONDO — il brano parte da solo: i browser vietano
   l'audio automatico CON volume prima di un tocco, quindi parte in
   silenzio e si attiva al primo tocco sulla pagina. Il pulsante
   resta per accendere/spegnere a mano.
   Per cambiare brano basta sostituire il file in frontend/public/musica/ */
export const MusicToggle = () => {
  const audioRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [on, setOn] = useState(false);
  const [hint, setHint] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/musica`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("musica"))))
      .then((d) => {
        if (d.src) setSrc(encodeURI(d.src));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!src) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = true;
    audio.play().catch(() => {});
    const unlock = () => {
      const a = audioRef.current;
      if (a) {
        a.muted = false;
        a.play()
          .then(() => setOn(true))
          .catch(() => {});
      }
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [src]);

  /* il pulsante segue anche la musica avviata dalla modalità film */
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setOn(true);
    const onPause = () => setOn(false);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, [src]);

  const toggle = async () => {
    if (!src) {
      setHint(true);
      setTimeout(() => setHint(false), 3400);
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (on) {
        audio.pause();
        setOn(false);
      } else {
        audio.muted = false;
        await audio.play();
        setOn(true);
      }
    } catch {
      setOn(false);
    }
  };

  return (
    <>
      {src && <audio ref={audioRef} id="bg-music" src={src} loop preload="auto" />}
      <motion.button
        className={`music-float ${on ? "music-float--on" : ""}`}
        onClick={toggle}
        aria-label={on ? "Disattiva musica di sottofondo" : "Attiva musica di sottofondo"}
        aria-pressed={on}
        data-testid="music-toggle-btn"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 260, damping: 16 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {on ? (
          <span className="eq" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </span>
        ) : (
          <Music size={19} />
        )}
        <span className="music-ring" aria-hidden="true" />
      </motion.button>
      <AnimatePresence>
        {hint && (
          <motion.span
            className="music-hint"
            data-testid="music-hint"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            Carica il brano in public/musica ✦
          </motion.span>
        )}
      </AnimatePresence>
    </>
  );
};
